import { styled } from '@mui/material';
import { useDialog } from 'contexts/dialog';
import { useViewport } from 'contexts/viewport';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Icon,
    IconButton,
    MenuItem,
    Paper,
    RichTextArea,
    Select,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from 'shell/components';
import {
    CITATION_SELECT_OPTIONS,
    CONTENT_BANK_RESOURCES_MB_LIMIT,
    DESCRIPTION_WORD_COUNT_LIMIT,
} from 'utils/constants';
import { bytesToMB, getHTMLWordCount } from 'utils/helper';

const GeneralSection = memo(
    ({
        isEditable,
        form,
        setForm,
        errors,
        setErrors,
        setSelectRubricDialog,
        setEditRubricDialog,
        setUploadContentBankResourcesDialog,
        setEditContentBankResourcesDialog,
        handleResyncAPIAssignment,
        forceRTEUpdate,
    }) => {
        const { setDialog, closeDialog } = useDialog();
        const { device } = useViewport();
        const { t } = useTranslation('assignment');

        const updateForm = (key, value) => {
            setForm((current) => ({
                ...current,
                [key]: value,
            }));
        };

        const handleClickResync = () => {
            setDialog({
                open: true,
                message: t('dialog.resync-assignment.title', 'Resync This Assignment?'),
                description: (
                    <Stack spacing={4}>
                        <Text>
                            {t(
                                'dialog.resync-assignment.description',
                                'This will replace the assignment name, description, and rubric with the latest version from your LMS.'
                            )}
                        </Text>

                        <Text>
                            {t(
                                'dialog.resync-assignment.note',
                                'Note: If you’ve already uploaded submissions, these will be deleted when you save/publish to ensure fair and consistent assessment based on the revised details.'
                            )}
                        </Text>
                    </Stack>
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.resync-from-lms', 'Resync from LMS'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: async () => {
                    closeDialog();
                    await handleResyncAPIAssignment();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        const handleClickEditRubric = () => {
            setEditRubricDialog({ open: true });
        };

        const handleClickDeleteRubric = () => {
            setDialog({
                open: true,
                message: t('dialog.delete-rubric.title', 'Delete Rubric?'),
                description: t('dialog.delete-rubric.description', 'Are you sure you want to delete this rubric?'),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        rubric: null,
                        feedbackSettings: { ...current.feedbackSettings, templated: null },
                        gradingSettings: null,
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        return (
            <RootStack sx={{ width: '100%' }} device={device} spacing={1}>
                <Stack alignItems={'center'} direction={'row'} spacing={5}>
                    <Text variant={'p1'}>{t('general-section.title', 'General Assignment Details')}</Text>

                    {form.api_assignment_id && (
                        <Button
                            color={'neutral'}
                            startIcon={<Icon kind={'resync'} />}
                            variant={'tertiary'}
                            onClick={handleClickResync}
                        >
                            {t('common:controls.resync-from-lms', 'Resync from LMS')}
                        </Button>
                    )}
                </Stack>
                <Paper variant={'outlined'}>
                    <Stack spacing={4}>
                        <Stack spacing={4}>
                            <CustomTextInput
                                error={errors.title}
                                placeholder={t(
                                    'general-section.assignment-title-placeholder',
                                    'Enter assignment title'
                                )}
                                value={form.title}
                                onChange={(e) => updateForm('title', e.target.value)}
                            />
                            <RichTextArea
                                forceRTEUpdate={forceRTEUpdate}
                                aria-label={'assignment description'}
                                disabled={!isEditable}
                                error={
                                    errors.description ||
                                    getHTMLWordCount(form.description) > DESCRIPTION_WORD_COUNT_LIMIT
                                }
                                label={t('general-section.assignment-description-label', 'Assignment Description')}
                                helperText={`${getHTMLWordCount(form.description)}/${DESCRIPTION_WORD_COUNT_LIMIT} words`}
                                helperTextPosition={'end'}
                                placeholder={t(
                                    'general-section.assignment-description-placeholder',
                                    'Enter assignment details'
                                )}
                                required
                                value={form.description}
                                onChange={(value) => updateForm('description', value)}
                            />
                            <Stack spacing={2}>
                                <Text>
                                    {t('assignment-rubric-label', 'Assignment Rubric')}{' '}
                                    <Text color={'error'} component={'span'}>
                                        *
                                    </Text>
                                </Text>
                                {form.rubric ? (
                                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                        <Text color={'light'}>{form.rubric.title}</Text>
                                        <Stack direction={'row'} spacing={2}>
                                            <IconButton
                                                aria-label={'edit rubric'}
                                                disabled={!isEditable}
                                                kind={'edit'}
                                                size={'small'}
                                                variant={'square'}
                                                onClick={handleClickEditRubric}
                                            />
                                            <IconButton
                                                aria-label={'delete rubric'}
                                                disabled={!isEditable}
                                                kind={'delete'}
                                                size={'small'}
                                                variant={'square'}
                                                onClick={handleClickDeleteRubric}
                                            />
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <>
                                        {form.api_assignment_id && (
                                            <CustomBox>
                                                <CustomText weight={'regular'}>
                                                    <Text component={'span'} color={'inherit'} weight={'medium'}>
                                                        {t(
                                                            'assignment.rubric.no-rubric-detected',
                                                            'No rubric detected'
                                                        )}
                                                        :{' '}
                                                    </Text>
                                                    {t(
                                                        'assignment.rubric.no-rubric-detected-description',
                                                        'A rubric is required for TimelyGrader to provide you with accurate grading/feedback suggestions. If you choose not to push this rubric back to your LMS, only the overall final grade will be passed back for each student.'
                                                    )}
                                                </CustomText>
                                            </CustomBox>
                                        )}
                                        <CustomButton
                                            color={errors.rubric ? 'destructive' : 'primary'}
                                            startIcon={<Icon kind={'add'} />}
                                            variant={'borderless'}
                                            onClick={() => setSelectRubricDialog({ open: true })}
                                        >
                                            {t('general-section.add-a-rubric', 'Add a Rubric')}
                                        </CustomButton>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                        <CustomSelect
                            disabled={!isEditable}
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text>{t('citation-format.label', 'Citation Format')}</Text>
                                    <Tooltip
                                        content={t(
                                            'citation-format.tooltip',
                                            'Grading adapts to your chosen citation style and applies the correct formatting standards (e.g., APA first-page formatting is evaluated differently).'
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            labelId={'citation-format-select'}
                            inputProps={{
                                'aria-label': `select a citation format`,
                            }}
                            value={form?.citationFormat}
                            onChange={(e) => updateForm('citationFormat', e.target.value)}
                        >
                            {CITATION_SELECT_OPTIONS.map(({ key, value }, index) => (
                                <MenuItem key={index} value={key}>
                                    {t(`citation-format.option.${key}`, value)}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text>{t('content-bank.title', 'Content Bank')}</Text>
                                <Tooltip
                                    content={t(
                                        'content-bank.tooltip',
                                        'Adding files to your Content Bank allows our AI to retrieve relevant context from these documents, enabling it to generate more personalized and accurate feedback and grading suggestions.'
                                    )}
                                    type={'info'}
                                />
                                <Text color={'light'}>
                                    [
                                    {t('content-bank.mb-used', '{{mbUsed}} MB used', {
                                        mbUsed: `${bytesToMB(
                                            form.contentBankResources
                                                ?.filter(({ active }) => active)
                                                .reduce((acc, cur) => acc + cur.data.size, 0) || 0
                                        )}/${CONTENT_BANK_RESOURCES_MB_LIMIT}`,
                                    })}
                                    ]
                                </Text>
                            </Stack>
                            {form.contentBankResources?.filter(({ active }) => active).length ? (
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text color={'light'}>
                                        {t('content-bank.files-added', '{{count}} file(s) added', {
                                            count: form.contentBankResources.filter(({ active }) => active).length,
                                        })}{' '}
                                    </Text>
                                    <Stack direction={'row'} spacing={2}>
                                        <IconButton
                                            aria-label={'edit content bank'}
                                            disabled={!isEditable}
                                            kind={'edit'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={() => setEditContentBankResourcesDialog({ open: true })}
                                        />
                                    </Stack>
                                </Stack>
                            ) : null}
                            <CustomButton
                                disabled={!isEditable}
                                startIcon={<Icon kind={'add'} />}
                                variant={'borderless'}
                                onClick={() => setUploadContentBankResourcesDialog({ open: true })}
                            >
                                {t('general-section.add-file', 'Add file')}
                            </CustomButton>
                        </Stack>
                    </Stack>
                </Paper>
            </RootStack>
        );
    }
);

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomTextInput = styled(TextInput)(({ theme }) => {
    return {
        margin: theme.spacing(-2, -3),
        width: `calc(100% + ${theme.spacing(6)})`,
        '&& input': {
            fontSize: `${theme.typography.sh1.fontSize.large}rem`,
            fontWeight: theme.typography.sh1.fontWeight.semibold,
        },
        '&& fieldset': {
            borderColor: 'transparent',
        },
    };
});

const CustomButton = styled(Button)(() => {
    return {
        width: 'fit-content',
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '8px',
        padding: theme.spacing(2, 3),
        background: theme.palette.ensightful.warning[100],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.warning[900],
    };
});

const CustomSelect = styled(Select)(() => {
    return {
        '& > .MuiInputBase-root': {
            maxWidth: '360px',
        },
    };
});

export default GeneralSection;
